import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { VdoHeaderComponent } from '@core/components/vdo-header/vdo-header.component';
import { IVdoSidebarConfig } from '@core/components/vdo-sidebar/interfaces/vdo-sidebar-config.interface';
import { VdoSidebarService } from '@core/components/vdo-sidebar/services/vdo-sidebar.service';
import { VdoSidebarComponent } from '@core/components/vdo-sidebar/vdo-sidebar.component';
import { AuthFeaturesService } from '@core/services/auth-features.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, shareReplay, startWith, Subject, takeUntil } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    VdoSidebarComponent,
    VdoHeaderComponent,
    AsyncPipe,
    NgIf,
    MatSidenavContent,
    MatSidenav,
    MatSidenavContainer,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'VDO On Board';
  isOpen: boolean = false;
  loginTemplate: 'empty' | 'main' = 'empty';
  coreBackgroundColor = signal<string | null>(null);

  private readonly cDRef = inject(ChangeDetectorRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly translateService = inject(TranslateService);
  private readonly vdoSidebarService = inject(VdoSidebarService);
  private readonly authFeaturesService = inject(AuthFeaturesService);

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  readonly routeData = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let snap = this.router.routerState.snapshot.root;
        let data: Record<string, any> = {};
        while (snap) {
          data = { ...data, ...snap.data };
          snap = snap.firstChild!;
        }
        return data as { template?: 'main' | 'empty'; basicLayout?: boolean };
      })
    ),
    { initialValue: { template: 'main', basicLayout: false } }
  );

  readonly isBasicLayout = computed(() => !!this.routeData().basicLayout);

  constructor() {
    this.translateService.addLangs(['pt-br', 'en', 'es']);
    this.translateService.use('pt-br');
  }

  ngOnInit(): void {
    this.authFeaturesService.initialize();
    this.isOpnGet();
    this.isLoginTemplate();
    this.observeBackgroundColor();
  }

  sidenavConfig$Get(): Observable<IVdoSidebarConfig> {
    return this.vdoSidebarService.sidenavConfig$;
  }

  toggleGet() {
    return this.vdoSidebarService.toggle();
  }

  private isOpnGet() {
    this.vdoSidebarService.isOpen$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (isOpen: boolean) => {
        this.isOpen = isOpen;
        this.cDRef.detectChanges();
      },
      error: err => {
        console.error('Error', err);
      },
    });
  }

  private isLoginTemplate(): void {
    this.router.events
      .pipe(
        startWith(true),
        filter(event => event instanceof NavigationEnd),
        shareReplay(1)
      )
      .subscribe({
        next: () => {
          const loginTemplateNow = this.shouldHideMenus() ? 'empty' : 'main';
          if (loginTemplateNow === 'main' && this.loginTemplate === 'empty') {
            this.openMenu();
          }
          this.loginTemplate = loginTemplateNow;
          this.cDRef.detectChanges();
        },
        error: err => {
          console.error('Error', err);
        },
      });
  }

  private observeBackgroundColor(): void {
    this.router.events
      .pipe(
        startWith(true),
        filter(event => event instanceof NavigationEnd),
        shareReplay(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.updateBackgroundColor(),
        error: err => console.error('Error observing background color', err),
      });
  }

  private updateBackgroundColor(): void {
    const color = this.findBackgroundColor(this.activatedRoute.root.snapshot);
    this.coreBackgroundColor.set(color);
  }

  private findBackgroundColor(routeSnapshot: ActivatedRouteSnapshot | null): string | null {
    if (!routeSnapshot) return null;
    const current = routeSnapshot.data?.['coreBackgroundColor'] ?? null;
    if (current) return current as string;
    for (const child of routeSnapshot.children) {
      const found = this.findBackgroundColor(child);
      if (found) return found;
    }
    return null;
  }

  /**
   * Determines whether navigation menus should be hidden based on route configuration.
   * Recursively checks the current route and all its children for 'hideMenus' data property.
   *
   * @param routeSnapshot - The route snapshot to check, defaults to the active root route
   * @returns true if menus should be hidden, false otherwise
   */
  private shouldHideMenus(
    routeSnapshot: ActivatedRouteSnapshot = this.activatedRoute.root.snapshot
  ): boolean {
    if (!routeSnapshot) {
      return false;
    }

    const shouldHideForCurrentRoute = !!routeSnapshot.data?.['hideMenus'];
    if (shouldHideForCurrentRoute) {
      return true;
    }

    return routeSnapshot.children.some(childRoute => this.shouldHideMenus(childRoute));
  }

  /**
   * Opens the menu and triggers change detection.
   */
  private openMenu(): void {
    this.vdoSidebarService.open();
  }
}
